import {
  Box,
  CircularProgress,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { ReactNode, useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import { Button } from "src/components/common/FormComponents/Buttons";
import EmptyView from "src/components/common/EmptyView";
import StatusBarChart from "src/components/common/StatusBarChart";
import NightingaleChart from "src/components/common/NightingaleChart";
import AmountHeatmap from "src/components/common/AmountHeatmap";
import HighlightSlider from "src/components/common/HighlightSlider";
import { getFundDashboardStats } from "src/utils/api/dashboard";
import { getStates } from "src/utils/api/roles";
import { downloadSheet, formatINR, generateExportFilename } from "src/utils/helper";

// ── Palette (validated for CVD in light mode via dataviz validator) ──────────
const TEAL = "#16A085"; // brand / series A / single-series magnitude
const AMBER = "#B26A00"; // categorical series B
const INK = "#1F2937";
const MUTED = "#6B7280";
const GRID = "#ECECEC";
const BLUE = "#3457D5"; // 3rd categorical hue (validated with TEAL/AMBER)
const SERIES3 = [TEAL, AMBER, BLUE]; // released / utilised / progress etc.

const FINANCIAL_YEARS = ["2024-25", "2025-26", "2026-27", "2027-28", "2028-29"];

/** Compact ₹ formatter for axes/labels (₹1.2Cr, ₹3.4L, ₹5.6K). */
const compactINR = (n: number) => {
  const v = Number(n) || 0;
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(2)}L`;
  if (a >= 1e3) return `${sign}₹${(a / 1e3).toFixed(1)}K`;
  return `${sign}₹${a}`;
};

/** Card shell so every chart reads as one system. */
const ChartCard = ({
  title,
  subtitle,
  children,
  height = 320,
  fullWidth = false,
  autoHeight = false,
  fillHeight = false,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  height?: number;
  fullWidth?: boolean;
  /** Let the card grow with its content instead of a fixed plot height. */
  autoHeight?: boolean;
  /** Stretch the card to fill its parent's height (plot area flexes). */
  fillHeight?: boolean;
}) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "10px",
      p: 2,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      border: "1px solid #F0F0F0",
      ...(fullWidth && { gridColumn: "1 / -1" }),
      ...(fillHeight && {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }),
    }}
  >
    <Box sx={{ fontSize: "15px", fontWeight: 600, color: INK }}>{title}</Box>
    {subtitle && (
      <Box sx={{ fontSize: "12px", color: MUTED, mb: 1 }}>{subtitle}</Box>
    )}
    <Box
      sx={{
        width: "100%",
        mt: 1,
        ...(fillHeight
          ? { flex: 1, minHeight: 0 }
          : { height: autoHeight ? "auto" : height }),
      }}
    >
      {children}
    </Box>
  </Box>
);

/** Dashboard filter dropdown.
 *
 *  The label is a sibling above the control, NOT MUI's floating `label` prop.
 *  The app's theme (styles/theme.ts) overrides MuiInputLabel with
 *  `transform: "none"` and a `marginBottom`, which is right for the
 *  label-above-field forms used everywhere else (see FormComponents/Select)
 *  but disables the shrink transform that lifts a floating label into the
 *  outlined input's notch — so a `label` on an outlined TextField renders
 *  straight through the top border instead of sitting in a gap.
 *
 *  `displayEmpty` so the "All" option shows as the resting value rather than
 *  leaving the field looking blank when no filter is applied. */
const FilterSelect = ({
  label,
  value,
  onChange,
  minWidth,
  children,
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  minWidth: number;
  children: ReactNode;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", minWidth }}>
    <InputLabel
      shrink
      sx={{
        fontSize: "12px",
        fontWeight: 600,
        color: MUTED,
        mb: 0.5,
        lineHeight: 1.2,
      }}
    >
      {label}
    </InputLabel>
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ displayEmpty: true }}
      sx={{ "& .MuiInputBase-root": { borderRadius: "8px" } }}
    >
      {children}
    </TextField>
  </Box>
);

/** Hero-number KPI tile (no plot — magnitude reads best as a big number). */
const KpiTile = ({
  label,
  value,
  meta,
  sub,
  subAlign = "left",
  accent = TEAL,
}: {
  label: string;
  value: string;
  /** Fund applications (or blocks) and net planted area behind the figure —
   *  the scale a rupee total on its own does not carry. One line per entry,
   *  stacked at the tile's right edge. */
  meta?: string[];
  /** Optional caption naming the population/basis behind the number, so a
   *  ratio cannot be read without seeing what it is measured over. */
  sub?: string;
  /** Alignment for `sub` — "right" sits it under the meta stack, at the
   *  tile's bottom-right corner. Defaults to left, under the label. */
  subAlign?: "left" | "right";
  accent?: string;
}) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "10px",
      p: 2,
      borderLeft: `4px solid ${accent}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        fontSize: "12px",
        color: MUTED,
        fontWeight: 500,
        mb: 0.5,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </Box>
    {/* Big number flush left, its scale stacked at the right edge. columnGap
        + wrap means the stack drops below the value rather than crushing it
        when the tile is narrow (2-up on phones). */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        columnGap: 1,
      }}
    >
      <Box sx={{ fontSize: "22px", fontWeight: 700, color: INK }}>{value}</Box>
      {!!meta?.length && (
        <Box
          sx={{
            ml: "auto",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {meta.map((line) => (
            <Box
              key={line}
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: INK,
                opacity: 0.72,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {line}
            </Box>
          ))}
        </Box>
      )}
    </Box>
    {sub && (
      <Box
        sx={{
          fontSize: "12px",
          color: MUTED,
          mt: 0.4,
          textAlign: subAlign,
        }}
      >
        {sub}
      </Box>
    )}
  </Box>
);

/** "1,234 FAs" / "1 FA" — the fund-application count behind a KPI figure.
 *  "FA" (Fund Application) matches the abbreviation used elsewhere on this
 *  page (the FA Year filter, FA1-FA10) rather than the generic "app". */
const countLabel = (n: number, one: string, many: string) =>
  `${(Number(n) || 0).toLocaleString("en-IN")} ${n === 1 ? one : many}`;
/** Whole hectares — sub-hectare precision is noise at KPI-tile size. */
const haLabel = (n: number) =>
  `${Math.round(Number(n) || 0).toLocaleString("en-IN")} ha`;
/** Fund-application count and area as two stacked lines beside a KPI value. */
const faAndArea = (n: number, area: number) => [
  countLabel(n, "FA", "FAs"),
  haLabel(area),
];
const blocksAndArea = (n: number, area: number) => [
  countLabel(n, "block", "blocks"),
  haLabel(area),
];
/** Count only, no area line — for tiles whose ₹ figure is a subset (a status,
 *  a money bucket) rather than a count naturally tied to hectares. */
const faOnly = (n: number) => [countLabel(n, "FA", "FAs")];
const blocksOnly = (n: number) => [countLabel(n, "block", "blocks")];

const axisTick = { fontSize: 12, fill: MUTED };
/** Ticks for the quarter-width column charts, where 12px collides. */
const axisTickSm = { fontSize: 10, fill: MUTED };
/** Height of the four side-by-side ranking charts. Ten categories at
 *  barSize 14 need the extra room a quarter-width column loses. */
const COLUMN_CHART_H = 420;
/** Height of the three side-by-side programme-level ₹ charts. */
const THIRD_CHART_H = 330;
const currencyTooltip = (v: any, name: any) => [compactINR(Number(v)), name];

type FundDashboardProps = {
  embedded?: boolean;
};

const FundDashboard = ({ embedded = false }: FundDashboardProps) => {
  const [filters, setFilters] = useState<{
    financialYear?: string;
    faYear?: number;
    stateId?: string;
  }>({});

  const [states, setStates] = useState<any[]>([]);
  const [data, setData] = useState<any>(null);

  useQuery(["fundDashboardStates"], () => getStates(), {
    onSuccess: (res: AxiosResponse) => setStates(res?.data ?? []),
    refetchOnMount: true,
  });

  const { isLoading, isFetching } = useQuery(
    ["fundDashboardStats", filters],
    () =>
      getFundDashboardStats({
        ...(filters.financialYear && { financialYear: filters.financialYear }),
        ...(filters.faYear && { faYear: filters.faYear }),
        ...(filters.stateId && { stateId: filters.stateId }),
      }),
    {
      onSuccess: (res: AxiosResponse) => setData(res?.data ?? null),
      onError: (_e: AxiosError) => {},
      refetchOnMount: true,
    },
  );

  const summary = data?.summary;

  const funnelData = useMemo(
    () =>
      (data?.statusFunnel ?? []).map((s: any) => ({
        status: s.status,
        name: s.label,
        count: s.count,
        amount: s.amount,
        area: s.area,
        // Land parcels (eco-restoration blocks) behind this status's area —
        // already de-duplicated per block server-side (P0-2: a block's
        // hectares are attributed to its latest FA's status, once).
        blocks: s.blocks,
      })),
    [data],
  );

  const financialsData = useMemo(
    () =>
      data?.financials
        ? [
            { name: "Requested", value: data.financials.requested },
            { name: "Estimated", value: data.financials.estimated },
            { name: "Released", value: data.financials.released },
            { name: "Utilized", value: data.financials.utilized },
            { name: "Unutilised", value: data.financials.unutilised },
          ]
        : [],
    [data],
  );

  const costHeadData = useMemo(() => {
    const r = data?.costHeads?.requested;
    const e = data?.costHeads?.estimated;
    if (!r || !e) return [];
    return [
      { name: "Advance Work", Requested: r.advanceWork, Estimated: e.advanceWork },
      { name: "Raising", Requested: r.raising, Estimated: e.raising },
      { name: "Maintenance", Requested: r.maintenance, Estimated: e.maintenance },
      { name: "Monitoring", Requested: r.monitoring, Estimated: e.monitoring },
      { name: "Contingency", Requested: r.contingency, Estimated: e.contingency },
    ];
  }, [data]);

  // #4 UC compliance — derived from the pipeline status counts.
  const ucComplianceData = useMemo(() => {
    const byStatus: Record<string, number> = {};
    (data?.statusFunnel ?? []).forEach((s: any) => {
      byStatus[s.status] = s.count;
    });
    const approved = byStatus["UC_APPROVED"] ?? 0;
    const uploaded = byStatus["UC_UPLOADED"] ?? 0;
    // Rejected needs a re-upload, so it is outstanding work like "not filed" —
    // shown as its own slice, but explicitly inside the same denominator.
    const rejected = byStatus["UC_REJECTED"] ?? 0;
    // Released but no UC filed at all.
    const notFiled =
      (byStatus["APPROVED"] ?? 0) + (byStatus["PAYMENT_ACKNOWLEDGED"] ?? 0);
    // The denominator is every released application — the four slices below
    // partition exactly the RELEASED_STATUSES set the backend uses.
    return [
      { name: "UC Approved", value: approved, color: "#2E7D32" },
      { name: "UC Uploaded", value: uploaded, color: "#2a78d6" },
      { name: "UC Rejected — re-upload due", value: rejected, color: "#C62828" },
      { name: "UC Not Filed", value: notFiled, color: "#9CA3AF" },
    ].filter((d) => d.value > 0);
  }, [data]);

  const fundFlowData = data?.fundFlow ?? [];
  const accountability = data?.accountability ?? {
    released: 0,
    utilised: 0,
    financialProgress: 0,
    utilizationPct: 0,
    progressCoveragePct: null,
  };
  const accountabilityData = [
    { name: "Released", value: accountability.released },
    { name: "Utilised", value: accountability.utilised },
    { name: "Fin. Progress", value: accountability.financialProgress },
  ];
  const divisionData = data?.division ?? [];
  const entityData = data?.entity ?? [];
  const exposureData = data?.exposure ?? [];

  // 22 auto-rotating highlight slides for the ticker under Financial Summary.
  const highlightSlides = useMemo(() => {
    if (!data || !summary) return [];
    const sf: Record<string, number> = {};
    (data.statusFunnel ?? []).forEach((s: any) => {
      sf[s.status] = s.count;
    });
    const ra = data.recentActivity ?? {};
    const geo = (data.geography ?? [])
      .slice()
      .sort((a: any, b: any) => b.released - a.released);
    const topState = geo[0];
    const topDivision = (data.division ?? [])[0];
    const topEntity = (data.entity ?? [])[0];
    const req = data.costHeads?.requested ?? {};
    const headLabels: Record<string, string> = {
      advanceWork: "Advance Work",
      raising: "Raising / Creation",
      maintenance: "Maintenance",
      monitoring: "Monitoring",
      contingency: "Contingency",
    };
    const biggestHead = Object.entries(req).sort(
      (a: any, b: any) => Number(b[1]) - Number(a[1]),
    )[0];
    const ecoCost = data.fundFlow?.[0]?.value ?? 0;
    const outstanding = Math.max(0, ecoCost - (summary.totalReleased ?? 0));
    const age90 =
      (data.ageing ?? []).find((a: any) => a.bucket === "90+ days")?.count ?? 0;
    // null when an FA-year filter is active — targets carry no FA year, so the
    // progress side cannot be narrowed to match the release side.
    const prog = data.accountability?.progressCoveragePct ?? null;

    const T = "#16A085"; // teal
    const B = "#2F6FED"; // blue
    const G = "#2E7D32"; // green
    const A = "#C77A00"; // amber
    const R = "#C62828"; // red (risk)

    const slides = [
      // A. momentum
      { title: "Applications created this month", value: `${ra.createdThisMonth ?? 0}`, accent: T, icon: "🆕" },
      { title: "Approved & released this month", value: `${ra.approvedThisMonth ?? 0}`, accent: G, icon: "✅" },
      { title: "Released this month", value: compactINR(ra.releasedThisMonth ?? 0), accent: B, icon: "💸" },
      { title: "Raised to admin this month", value: `${ra.raisedThisMonth ?? 0}`, accent: T, icon: "📤" },
      { title: "New this week", value: `${ra.createdThisWeek ?? 0}`, accent: A, icon: "🗓️" },
      // B. volume & status
      { title: "Total fund applications", value: `${summary.totalApplications}`, accent: B, icon: "📊" },
      { title: "Awaiting admin action", value: `${sf.RAISED ?? 0}`, accent: A, icon: "⏳" },
      { title: "Under query", value: `${(sf.UNDER_QUERY ?? 0) + (sf.NODAL_UNDER_QUERY ?? 0)}`, accent: A, icon: "❓" },
      { title: "Payment acknowledged", value: `${sf.PAYMENT_ACKNOWLEDGED ?? 0}`, accent: G, icon: "🧾" },
      { title: "Rejected applications", value: `${sf.REJECTED ?? 0}`, accent: R, icon: "🚫" },
      // C. money
      { title: "Released to date", value: compactINR(summary.totalReleased), accent: T, icon: "💰" },
      { title: "Fund utilisation", value: `${summary.utilizationPct}%`, accent: G, icon: "📈" },
      { title: "Unutilised", value: compactINR(summary.unutilised), sub: "Pending UC's", accent: B, icon: "🏦" },
      { title: "Approval haircut", value: `${summary.approvalHaircutPct}%`, sub: "cut from requested", accent: A, icon: "✂️" },
      { title: "Financial-progress coverage", value: prog === null ? "—" : `${prog}%`, sub: prog === null ? "not comparable under an FA filter" : "of released money", accent: G, icon: "🌱" },
      // D. leaders
      { title: "Top state by released", value: topState?.state ?? "—", sub: topState ? compactINR(topState.released) : "", accent: T, icon: "🏆" },
      { title: "Top forest division", value: topDivision?.division ?? "—", sub: topDivision ? compactINR(topDivision.released) : "", accent: G, icon: "🌳" },
      { title: "Top entity / agency", value: topEntity?.entity ?? "—", sub: topEntity ? compactINR(topEntity.released) : "", accent: B, icon: "🥇" },
      { title: "Biggest cost head", value: biggestHead ? headLabels[biggestHead[0]] ?? biggestHead[0] : "—", sub: biggestHead ? compactINR(Number(biggestHead[1])) : "", accent: A, icon: "📌" },
      // E. risk / attention
      { title: "Waiting 90+ days", value: `${age90}`, sub: "need attention", accent: R, icon: "⚠️" },
      { title: "Outstanding", value: compactINR(outstanding), sub: "committed, not yet released", accent: A, icon: "📉" },
      { title: "UC rejected", value: `${sf.UC_REJECTED ?? 0}`, sub: "need re-upload", accent: R, icon: "🔁" },
    ];
    return slides;
  }, [data, summary]);

  const geoHeatmap = data?.geographyHeatmap ?? {
    states: [],
    years: [],
    max: 0,
    groups: [],
  };

  /** Pipeline chart data in the shape the shared StatusBarChart expects
   *  (uv = Number, pv = Area, amount = Requested ₹ — the optional 3rd series)
   *  — same component used by the Market Watch "Status of Payment and Fund
   *  Application" chart, so the Fund Dashboard's pipeline matches it exactly
   *  (dual/triple-axis, legend toggle, click-to-focus, wrapping labels).
   *
   *  The leading "Created" bar carries the whole pipeline — every application
   *  that was created and did not end up Rejected — rather than only the few
   *  still sitting at CREATED. StatusBarChart treats rows[0] as the 100% base
   *  for the share it prints in each tooltip, so that first bar has to be the
   *  funnel's mouth for those percentages to mean anything.
   *
   *  Area (and the parcel count alongside it) sums cleanly here: the backend
   *  attributes each block's hectares to exactly one status (its latest FA),
   *  so adding statuses adds disjoint sets of blocks rather than counting any
   *  block twice. Only the chart is rolled up — `data.statusFunnel` itself
   *  stays a pure per-status breakdown, so the Excel export's rows still sum
   *  to the true total.
   *
   *  `parcels` (land-parcel/block count) rides alongside `amount` so
   *  StatusBarChart's "Created: …" summary line can report parcels instead of
   *  ₹ — the bars/axis/legend still show the requested amount unchanged. */
  const pipelineChartData = useMemo(() => {
    if (!funnelData.length) return [];
    const total = funnelData
      .filter((f: any) => f.status !== "REJECTED")
      .reduce(
        (acc: any, f: any) => ({
          count: acc.count + (Number(f.count) || 0),
          area: acc.area + (Number(f.area) || 0),
          amount: acc.amount + (Number(f.amount) || 0),
          blocks: acc.blocks + (Number(f.blocks) || 0),
        }),
        { count: 0, area: 0, amount: 0, blocks: 0 },
      );
    return funnelData.map((f: any) =>
      f.status === "CREATED"
        ? {
            name: f.name,
            uv: total.count,
            pv: +total.area.toFixed(2),
            amount: total.amount,
            parcels: total.blocks,
          }
        : {
            name: f.name,
            uv: f.count,
            pv: f.area,
            amount: f.amount,
            parcels: f.blocks,
          },
    );
  }, [funnelData]);

  const handleExport = () => {
    if (!data) return;
    /* One row shape across all three sections. The sheet is built by
       json_to_sheet, which takes its columns and widths from the first object
       it sees — so ragged sections silently lost their extra columns and
       mis-sized the rest. Every row now carries the same keys, blank where a
       section has nothing to say. */
    const row = (
      section: string,
      label: string,
      count: number,
      v: {
        requested?: number;
        released?: number;
        utilized?: number;
        area?: number;
      },
    ) => ({
      Section: section,
      Label: label,
      Count: count,
      "Requested (₹)":
        v.requested === undefined ? "" : formatINR(v.requested),
      "Released (₹)": v.released === undefined ? "" : formatINR(v.released),
      "Utilized (₹)": v.utilized === undefined ? "" : formatINR(v.utilized),
      "Net Planted Area (ha)": v.area === undefined ? "" : v.area,
    });

    const statusSheet = (data.statusFunnel ?? []).map((s: any) =>
      row("Status", s.label, s.count, {
        requested: s.amount,
        released: s.released,
        area: s.area,
      }),
    );
    const yearSheet = (data.multiYear ?? []).map((m: any) =>
      row(`FA${m.faYear}`, `Year ${m.faYear}`, m.count, {
        requested: m.requested,
        released: m.released,
        utilized: m.utilized,
      }),
    );
    const geoSheet = (data.geography ?? []).map((g: any) =>
      row("State", g.state, g.count, {
        requested: g.requested,
        released: g.released,
      }),
    );
    downloadSheet(
      [...statusSheet, ...yearSheet, ...geoSheet],
      generateExportFilename("fund-dashboard"),
    );
  };

  const loading = isLoading || isFetching;

  const content = (
    <Box sx={{ pb: 4 }}>
        {/* Filters + export — one row above the charts */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            // flex-end, not center: the selects are now label-above-control, so
            // centring would float the Export button against their mid-height.
            alignItems: "flex-end",
            background: "#fff",
            p: 1.5,
            borderRadius: "10px",
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          {/* Label above the control, never MUI's floating `label` prop: the
              app's theme sets `transform: none` + `marginBottom` on
              MuiInputLabel (styles/theme.ts), which disables the shrink
              animation that lifts a floating label into the outline's notch.
              With it disabled, a `label` on an outlined TextField renders
              straight through the top border. Every other form in the app uses
              the same label-above pattern (see FormComponents/Select). */}
          <FilterSelect
            label="Financial Year"
            value={filters.financialYear ?? ""}
            onChange={(v) =>
              setFilters((f) => ({ ...f, financialYear: v || undefined }))
            }
            minWidth={160}
          >
            <MenuItem value="">All</MenuItem>
            {FINANCIAL_YEARS.map((fy) => (
              <MenuItem key={fy} value={fy}>
                {fy}
              </MenuItem>
            ))}
          </FilterSelect>

          <FilterSelect
            label="FA Year"
            value={filters.faYear ?? ""}
            onChange={(v) =>
              setFilters((f) => ({ ...f, faYear: v ? Number(v) : undefined }))
            }
            minWidth={140}
          >
            <MenuItem value="">All</MenuItem>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((y) => (
              <MenuItem key={y} value={y}>
                {`FA ${y}`}
              </MenuItem>
            ))}
          </FilterSelect>

          <FilterSelect
            label="State"
            value={filters.stateId ?? ""}
            onChange={(v) =>
              setFilters((f) => ({ ...f, stateId: v || undefined }))
            }
            minWidth={180}
          >
            <MenuItem value="">All States</MenuItem>
            {states.map((s: any) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </FilterSelect>

          <Box sx={{ flex: 1 }} />
          <Button text="Export" onClick={handleExport} disabled={!data} />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !data || !summary?.totalApplications ? (
          <EmptyView
            heading="No fund applications found"
            subheading="Adjust the filters above."
            onClick={() => {}}
          />
        ) : (
          <>
            {/* KPI tiles */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 1.5,
                mb: 2,
              }}
            >
              <KpiTile
                label="Total Applications"
                value={`${summary.totalApplications}`}
                meta={blocksAndArea(summary.blocks, summary.areaTotal)}
                sub="across all eco-restoration blocks"
              />
              {/* Matches the Admin Dashboard's "Demand Note Raised" column
                  group to the rupee: every block whose demand note is raised
                  (not just those with a fund application), total_area, and
                  demand_note + icfre_charges. It reads higher than the
                  Eco-Restoration Cost tile beside it for exactly those three
                  reasons — same underlying demand notes, wider scope and a
                  charge added on. */}
              <KpiTile
                label="Demand Note Raised"
                value={compactINR(summary.demandNoteAmount)}
                meta={blocksOnly(summary.demandNoteCount)}
                sub="incl. ICFRE charges · all raised blocks"
              />
              {/* SUM(land_ownership.demand_note) over DISTINCT blocks — the
                  demand-note total. The schema's `eco_restoration_cost` column
                  is a different quantity (what entities have actually paid in;
                  Monitoring shows it as "Eco-Restoration Cost Paid"), so it is
                  deliberately not the source here. */}
              <KpiTile
                label="Total Eco-Restoration Cost"
                value={compactINR(summary.ecoRestorationCost)}
                meta={blocksOnly(summary.blocks)}
                sub="demand-note total"
              />
              <KpiTile
                label="All Fund Application Detail"
                value={compactINR(summary.totalRequested)}
                meta={faAndArea(summary.requestedCount, summary.areaTotal)}
              />
              <KpiTile
                label="Total Released by Admin"
                value={compactINR(summary.totalReleased)}
                meta={faOnly(summary.releasedAppCount)}
                accent={AMBER}
              />
              <KpiTile
                label="Total Utilized"
                value={compactINR(summary.totalUtilized)}
                meta={faOnly(summary.ucFiledCount)}
                sub="As per received UC's"
                subAlign="right"
                accent={AMBER}
              />
              {/* Two tiles, not one "Balance". Released − Utilized and
                  Eco-restoration cost − Released answer different questions and
                  were previously conflated into a single figure that summed
                  fund_detail.amount_left — a per-block cumulative column that
                  double-counts once a block has more than one FA. */}
              <KpiTile
                label="Unutilised"
                value={compactINR(summary.unutilised)}
                meta={faOnly(summary.unutilisedCount)}
                sub="Pending UC's"
              />
              {/* Same definition the Fund Application detail page uses for
                  "Balance Amount": demand note − everything released to date. */}
              <KpiTile
                label="Balance Amount"
                value={compactINR(summary.unreleasedCommitment)}
                meta={blocksOnly(summary.blocksUnreleased)}
              />
              {/* Utilization % and Approval Haircut % are no longer tiles —
                  both still ride the highlights ticker. */}
            </Box>

            {/* Charts grid — 10 cols so a row can split 30% / 70%. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "repeat(10, 1fr)" },
                gap: 2,
                alignItems: "start",
              }}
            >
              {/* Status funnel — reuses the same shared StatusBarChart as the
                  Market Watch "Status of Payment and Fund Application" chart,
                  so the pipeline view is visually consistent across the app:
                  dual-axis (Number / Area), toggleable legend, click-to-focus. */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <StatusBarChart
                  data={pipelineChartData}
                  label="Fund Application Status — Count, Net Planted Area & Amount"
                  // The ₹ series carries total_cost (requested), not released —
                  // naming it plainly stops it being read against the Total
                  // Released KPI, which spans several statuses.
                  amountLabel="Requested (₹)"
                />
              </Box>

              {/* One container, two columns: Highlights / Financial Summary /
                  UC Compliance stacked on the left, the States heatmap beside
                  them stretched to the same height, so both columns end on the
                  same line instead of the heatmap stopping short. Whichever
                  column is taller sets the row height and the other fills it.
                  30/70 split — the left column's content (ticker, rose chart,
                  donut) stays legible narrow, while the heatmap (up to 2
                  sub-tables × state name + 5 FA-year columns + Total, each)
                  needs the room: at 40/60 its two sub-tables' combined minimum
                  width could exceed the share it was given, forcing a
                  horizontal scroll inside the card on real data. */}
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "3fr 7fr" },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                {/* Financials — Nightingale/rose chart: one wedge per measure,
                    radius encodes magnitude (sqrt-scaled so wedge AREA stays
                    proportional to value), the standout measure in accent teal,
                    the rest in gray context. */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  {/* Auto-sliding highlights — fixed height on top */}
                  <Box sx={{ height: 230, flexShrink: 0 }}>
                    <HighlightSlider slides={highlightSlides} />
                  </Box>
                  {/* Financial Summary flex-grows to fill the rest, so the column
                      bottom aligns with the Top States card. */}
                  <Box sx={{ flex: 1, minHeight: 320 }}>
                    <ChartCard
                      title="Financial Summary"
                      subtitle="Requested vs Estimated vs Released vs Utilized vs Unutilised (₹)"
                      fillHeight
                    >
                      <Box sx={{ display: "flex", justifyContent: "center", height: "100%" }}>
                        <NightingaleChart data={financialsData} valueFormatter={compactINR} />
                      </Box>
                    </ChartCard>
                  </Box>
                  {/* #4 UC compliance donut — sits directly under Financial
                      Summary. Fixed height and no flex-grow, so Financial Summary
                      above still absorbs whatever slack the column has when the
                      States heatmap beside it runs tall. */}
                  <Box sx={{ flexShrink: 0 }}>
                    <ChartCard
                      title="UC Compliance"
                      subtitle="Utilisation-certificate status of released applications"
                      height={250}
                    >
                      {ucComplianceData.length === 0 ? (
                        <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: 13 }}>
                          No released applications yet.
                        </Box>
                      ) : (
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={ucComplianceData}
                              dataKey="value"
                              nameKey="name"
                              innerRadius="55%"
                              outerRadius="80%"
                              paddingAngle={2}
                              stroke="#fff"
                              strokeWidth={2}
                            >
                              {ucComplianceData.map((d, i) => (
                                <Cell key={i} fill={d.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any, n: any) => [v, n]} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </ChartCard>
                  </Box>
                </Box>

                {/* States by Amount Released — heatmap (states × FA years).
                    Every state with fund activity, not a top-N, split into 2
                    side-by-side sub-tables. fillHeight (not autoHeight) so the
                    card stretches to the left column's full height and the two
                    bottom edges line up. */}
                <Box sx={{ minWidth: 0 }}>
                  <ChartCard
                    title="States by Amount Released"
                    subtitle={`All ${geoHeatmap.states.length} states with fund activity — Released / Fin. Progress / Utilised / Unutilised (released − utilised) rows across FA-year columns (₹)`}
                    fillHeight
                  >
                    <AmountHeatmap
                      groups={geoHeatmap.groups}
                      years={geoHeatmap.years}
                      columns={2}
                      valueFormatter={compactINR}
                    />
                  </ChartCard>
                </Box>
              </Box>

              {/* Cost-head mix — commented out (replaced in this slot by the
                  Top States heatmap below).
              <ChartCard
                title="Cost-Head Mix"
                subtitle="Requested vs Estimated by cost head (₹)"
              >
                <ResponsiveContainer>
                  <BarChart
                    data={costHeadData}
                    margin={{ left: 10, right: 10, top: 12, bottom: 4 }}
                  >
                    <CartesianGrid vertical={false} stroke={GRID} />
                    <XAxis dataKey="name" tick={axisTick} />
                    <YAxis tick={axisTick} tickFormatter={compactINR} width={70} />
                    <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Requested" fill={TEAL} radius={[4, 4, 0, 0]} barSize={18} />
                    <Bar dataKey="Estimated" fill={AMBER} radius={[4, 4, 0, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              */}


              {/* Fund Flow, Accountability and Cost-Head Mix side by side in one
                  row — the three programme-level ₹ views, read together. Thirds
                  of a 10-column grid are not whole columns, so they get their
                  own 3-column grid spanning the full width. 1-up below lg. */}
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                {/* #1 Fund-flow funnel — Eco cost → Requested → Released → Utilised */}
                <ChartCard
                  title="Fund Flow"
                  subtitle="Eco-Restoration Cost → Requested → Released → Utilised (₹)"
                  height={THIRD_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart
                      layout="vertical"
                      data={fundFlowData}
                      margin={{ left: 0, right: 56, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid horizontal={false} stroke={GRID} />
                      <XAxis type="number" tick={axisTickSm} tickFormatter={compactINR} tickCount={4} />
                      <YAxis type="category" dataKey="stage" tick={axisTickSm} width={96} />
                      <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Bar dataKey="value" fill={TEAL} radius={[0, 4, 4, 0]} barSize={22}>
                        <LabelList dataKey="value" position="right" formatter={compactINR} style={{ fontSize: 10, fill: INK }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* #2 Accountability — Released vs Utilised vs Financial Progress */}
                <ChartCard
                  title="Released vs Utilised vs Financial Progress"
                  subtitle={`Utilisation ${accountability.utilizationPct}% · Progress coverage ${
                    accountability.progressCoveragePct === null
                      ? "— (not comparable under an FA-year filter)"
                      : `${accountability.progressCoveragePct}% of released (₹)`
                  }`}
                  height={THIRD_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart
                      data={accountabilityData}
                      margin={{ left: 0, right: 10, top: 16, bottom: 4 }}
                    >
                      <CartesianGrid vertical={false} stroke={GRID} />
                      <XAxis dataKey="name" tick={axisTickSm} />
                      <YAxis tick={axisTickSm} tickFormatter={compactINR} width={62} />
                      <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={44}>
                        {accountabilityData.map((_, i) => (
                          <Cell key={i} fill={SERIES3[i % SERIES3.length]} />
                        ))}
                        <LabelList dataKey="value" position="top" formatter={compactINR} style={{ fontSize: 10, fill: INK }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* #7 Cost-head mix */}
                <ChartCard
                  title="Cost-Head Mix"
                  subtitle="Requested vs Estimated by cost head (₹)"
                  height={THIRD_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart data={costHeadData} margin={{ left: 0, right: 10, top: 12, bottom: 4 }}>
                      <CartesianGrid vertical={false} stroke={GRID} />
                      <XAxis dataKey="name" tick={axisTickSm} interval={0} />
                      <YAxis tick={axisTickSm} tickFormatter={compactINR} width={62} />
                      <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Requested" fill={TEAL} radius={[4, 4, 0, 0]} barSize={12} />
                      <Bar dataKey="Estimated" fill={AMBER} radius={[4, 4, 0, 0]} barSize={12} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Box>

              {/* The four state / division / agency breakdowns, side by side in
                  one row. They all rank the same ten-ish categories on a ₹ axis,
                  so reading them together is the point — a quarter-width column
                  each, tall enough that every category still gets a labelled
                  bar. Falls back to 2-up on md and 1-up on xs, where a quarter
                  of the viewport would be unreadable. */}
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {/* #3 Outstanding exposure by state (Released + Outstanding = Demand) */}
                <ChartCard
                  title="Outstanding Exposure by State"
                  subtitle="Eco-restoration cost split into Released vs Outstanding (₹)"
                  height={COLUMN_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={exposureData} margin={{ left: 0, right: 12, top: 4, bottom: 4 }}>
                      <CartesianGrid horizontal={false} stroke={GRID} />
                      <XAxis type="number" tick={axisTickSm} tickFormatter={compactINR} tickCount={3} />
                      <YAxis type="category" dataKey="state" tick={axisTickSm} width={82} />
                      <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="released" name="Released" stackId="d" fill={TEAL} barSize={14} />
                      <Bar dataKey="outstanding" name="Outstanding" stackId="d" fill={AMBER} barSize={14} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* #13 ₹ released per hectare by state */}
                <ChartCard
                  title="Release Intensity (₹ / hectare)"
                  subtitle="Amount released per net planted hectare, by state"
                  height={COLUMN_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={exposureData} margin={{ left: 0, right: 44, top: 4, bottom: 4 }}>
                      <CartesianGrid horizontal={false} stroke={GRID} />
                      <XAxis type="number" tick={axisTickSm} tickFormatter={compactINR} tickCount={3} />
                      <YAxis type="category" dataKey="state" tick={axisTickSm} width={82} />
                      <Tooltip formatter={(v: any) => [`${compactINR(Number(v))}/ha`, "Intensity"]} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Bar dataKey="perHectare" fill={BLUE} radius={[0, 4, 4, 0]} barSize={14}>
                        <LabelList dataKey="perHectare" position="right" formatter={compactINR} style={{ fontSize: 9, fill: INK }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* #11 Division-wise released */}
                <ChartCard
                  title="Top Forest Divisions by Amount Released"
                  subtitle="Highest 10 divisions (₹)"
                  height={COLUMN_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={divisionData} margin={{ left: 0, right: 44, top: 4, bottom: 4 }}>
                      <CartesianGrid horizontal={false} stroke={GRID} />
                      <XAxis type="number" tick={axisTickSm} tickFormatter={compactINR} tickCount={3} />
                      <YAxis type="category" dataKey="division" tick={axisTickSm} width={96} />
                      <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Bar dataKey="released" fill={TEAL} radius={[0, 4, 4, 0]} barSize={14}>
                        <LabelList dataKey="released" position="right" formatter={compactINR} style={{ fontSize: 9, fill: INK }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* #12 Top entities / agencies */}
                <ChartCard
                  title="Top Entities by Amount Released"
                  subtitle="Highest 10 entities / agencies (₹)"
                  height={COLUMN_CHART_H}
                >
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={entityData} margin={{ left: 0, right: 44, top: 4, bottom: 4 }}>
                      <CartesianGrid horizontal={false} stroke={GRID} />
                      <XAxis type="number" tick={axisTickSm} tickFormatter={compactINR} tickCount={3} />
                      <YAxis type="category" dataKey="entity" tick={axisTickSm} width={96} />
                      <Tooltip formatter={currencyTooltip} cursor={{ fill: "rgba(22,160,133,0.06)" }} />
                      <Bar dataKey="released" fill={AMBER} radius={[0, 4, 4, 0]} barSize={14}>
                        <LabelList dataKey="released" position="right" formatter={compactINR} style={{ fontSize: 9, fill: INK }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Box>

            </Box>
          </>
        )}
    </Box>
  );

  return embedded ? (
    content
  ) : (
    <DashboardWrapper
      title="Fund Dashboard"
      showBtn={false}
      onButtonClick={() => {}}
    >
      {content}
    </DashboardWrapper>
  );
};

export default FundDashboard;
